import { useState } from "react";
import { Link } from "react-router-dom";
import books from "@/data/books";
import reviews from "@/data/reviews";
import authorData from "@/data/author";
import BookCard from "@/components/BookCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Star, ArrowRight, Palette, Gift, Quote, ChevronRight } from "lucide-react";

const Home = () => {
  const [filter, setFilter] = useState("All");
  const categories = ["All", "Religious End-Times", "Narrative Novel", "Coloring Book", "Journal", "Cookbook", "AI & Technology", "Self-Help", "Romance"];

  const filteredBooks = filter === "All" ? books : books.filter(b => b.category === filter);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="py-20 px-4 pt-28" data-testid="hero-section">
        <div className="container mx-auto text-center max-w-4xl">
          <Badge className="mb-4 bg-emerald-900 text-2xl py-2 px-6">
            <span className="font-bold animate-gradient-text" style={{
              background: 'linear-gradient(90deg, #fbbf24, #fbbf24, #cbd5e1, #cbd5e1, #fbbf24, #fbbf24)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'gradient-scroll 3s linear infinite'
            }}>Books Authored by Cameron Pityo</span>
          </Badge>
          <h2 className="text-5xl md:text-6xl font-serif font-bold mb-6 bg-gradient-to-r from-emerald-800 via-amber-600 to-emerald-800 bg-clip-text text-transparent drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.1)' }}>The Wonder Vault</h2>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            From soul to salty, from sweet to sour, black and white to color, from mystery to romance. 
            Books that linger in your mind long after the last page is turned.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              className="bg-emerald-900 hover:bg-emerald-800 px-8 gold-text-outline" 
              data-testid="explore-books-btn"
              onClick={() => document.getElementById('books')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Books <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Link to="/coloring-pages">
              <Button className="px-8 bg-amber-500 hover:bg-amber-600 green-text-outline" data-testid="coloring-pages-btn">
                <Palette className="mr-2 h-5 w-5" /> Coloring Pages
              </Button>
            </Link>
          </div>
          
          <div className="mt-12">
            <p className="text-center text-xs font-semibold text-amber-700 uppercase tracking-wider mb-3">Goals for Cornerstone Ink</p>
            <div className="flex gap-8 justify-center text-center">
              <div>
                <div className="text-3xl font-bold text-amber-700">50K+</div>
                <div className="text-sm text-slate-600">Copies Sold</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-700">15</div>
                <div className="text-sm text-slate-600">Published Books</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-700">10,000+</div>
                <div className="text-sm text-slate-600">Happy Readers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Book of the Month */}
      <section className="py-12 px-4 bg-gradient-to-r from-emerald-50 to-amber-50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-amber-500 text-emerald-900 text-lg py-2 px-6 animate-pulse">
              Featured Book of the Month
            </Badge>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-8 max-w-5xl mx-auto">
            <div className="md:w-1/3">
              <div className="relative">
                <img 
                  src="https://m.media-amazon.com/images/I/91BWLSIiBmL._SY522_.jpg" 
                  alt="The Secrets of Harbourside Bay"
                  className="rounded-xl shadow-2xl w-full max-w-xs mx-auto ring-4 ring-amber-400"
                />
                <div className="absolute -top-3 -right-3 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                  SALE!
                </div>
              </div>
            </div>
            <div className="md:w-2/3 text-center md:text-left">
              <Badge className="mb-4 bg-emerald-900 text-amber-500">Romance - Suspense - Drama</Badge>
              <h3 className="text-3xl font-serif font-bold text-slate-900 mb-4">The Secrets of Harbourside Bay</h3>
              <p className="text-lg text-slate-600 mb-4">
                A gripping tale of love, betrayal, obsession, and the fragile line between reality and perception. 
                In the quiet coastal town of Harbourside, Maine, Clara Adler's picture-perfect life begins to unravel 
                when secrets multiply and trust erodes.
              </p>
              <p className="text-slate-500 italic mb-6">
                "The most dangerous secrets aren't the ones we keep from others... They're the ones we convince ourselves are true."
              </p>
              <div className="flex gap-4 justify-center md:justify-start flex-wrap items-center">
                <div className="text-center">
                  <span className="text-3xl font-bold text-emerald-800">$5.99</span>
                  <div className="text-sm text-red-600 font-semibold">Limited Time Only!</div>
                </div>
                <a href="https://www.amazon.com/dp/B0GPW7N4JZ" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-amber-500 hover:bg-amber-600 px-8 text-emerald-900 font-bold" data-testid="featured-book-buy-btn">
                    Buy Now on Amazon <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Coloring Pages Banner */}
      <section className="py-12 px-4 bg-gradient-to-r from-amber-100 to-amber-50">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/3">
              <img 
                src="/covers/bible_heroes_main.png" 
                alt="Bible Heroes Coloring Pages"
                className="rounded-xl shadow-lg w-full max-w-sm mx-auto"
              />
            </div>
            <div className="md:w-2/3 text-center md:text-left">
              <Badge className="mb-4 bg-emerald-900 text-amber-500">NEW - Digital Downloads</Badge>
              <h3 className="text-3xl font-serif font-bold text-slate-900 mb-4">Bible Heroes Coloring Pages</h3>
              <p className="text-lg text-slate-600 mb-6">
                Bring Bible stories to life with our beautiful coloring page collections! Each pack contains 12-15 pages 
                featuring beloved characters like Noah, David, Moses, and more. Perfect for children's ministry, 
                homeschool, or family devotion time.
              </p>
              <div className="flex gap-4 justify-center md:justify-start flex-wrap">
                <Link to="/coloring-pages">
                  <Button className="bg-emerald-900 hover:bg-emerald-800 px-8 gold-text-outline" data-testid="view-coloring-packs-btn">
                    View All Packs <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Badge className="bg-amber-500 text-emerald-900 text-lg py-2 px-4">
                  <Gift className="inline h-4 w-4 mr-1" /> Complete Bundle $5.00!
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Books Section */}
      <section id="books" className="py-20 px-4 bg-slate-50" data-testid="books-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-serif font-bold text-slate-900 mb-4">Browse Our Collection</h3>
            <p className="text-slate-600 text-lg max-w-4xl mx-auto">
              Dive into a curated library of stories and knowledge. Whether you're seeking your next gripping escape, 
              a toolkit for personal growth, or expert insight into a fascinating subject, you'll find it here.
            </p>
          </div>
          
          <div className="flex gap-3 justify-center mb-12 flex-wrap">
            {categories.map((cat) => (
              <Button 
                key={cat}
                variant={filter === cat ? "default" : "outline"}
                className={filter === cat ? "bg-amber-700 hover:bg-amber-800" : "border-slate-300 hover:bg-slate-50"}
                onClick={() => setFilter(cat)}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </Button>
            ))}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredBooks.map((book) => (
              <BookCard key={book.id} book={book} />
            ))}
          </div>
        </div>
      </section>

      {/* About the Author Section */}
      <section id="about" className="py-20 px-4 bg-white" data-testid="about-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-900">Meet the Author</Badge>
            <h3 className="text-4xl font-serif font-bold text-slate-900">About Cameron Pityo</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-64 h-64 mx-auto rounded-full overflow-hidden border-4 border-amber-500 shadow-xl">
                <img 
                  src={authorData.image} 
                  alt={authorData.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-center mt-6">
                <Badge className="bg-emerald-900 text-amber-500 text-lg py-2 px-6">Author</Badge>
                <div className="text-sm text-black italic mt-2">Freumhan Albannach</div>
                <div className="text-sm text-black italic">"Scottish Roots"</div>
              </div>
            </div>
            <div>
              <h4 className="text-2xl font-serif font-bold text-slate-900 mb-4">{authorData.name}</h4>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">{authorData.bio}</p>
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-700">11+</div>
                  <div className="text-sm text-slate-500">Books Published</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-700">5000+</div>
                  <div className="text-sm text-slate-500">Happy Readers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-700">5+</div>
                  <div className="text-sm text-slate-500">Genres</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reader Reviews Section */}
      <section className="py-20 px-4 bg-slate-50" data-testid="reviews-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-500 text-emerald-900">Testimonials</Badge>
            <h3 className="text-4xl font-serif font-bold text-slate-900 mb-4">What Readers Are Saying</h3>
            <p className="text-lg text-slate-600">Discover why thousands of readers love Cameron's books</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review) => (
              <Card key={review.id} className="border-slate-200 hover:shadow-lg transition-shadow" data-testid={`review-card-${review.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{review.name}</CardTitle>
                      <CardDescription>on {review.book}</CardDescription>
                    </div>
                    <Quote className="h-6 w-6 text-amber-700" />
                  </div>
                  <div className="flex gap-1 mt-2">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 italic">"{review.quote}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-emerald-900">
        <div className="container mx-auto text-center max-w-3xl">
          <h3 className="text-4xl font-serif font-bold mb-4 gold-text-outline">Start Your Journey Today</h3>
          <p className="text-xl mb-8 gold-text-outline">
            Join thousands of readers who have discovered the transformative power of these stories.
          </p>
          <a href="#books">
            <Button className="bg-amber-500 text-emerald-900 hover:bg-amber-600 px-8 font-semibold" data-testid="browse-all-btn">
              Browse All Books <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
